import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { SearchIcon } from 'cat-ecommerce-alloy';
import { useBreakpoint } from '@app/hooks';
import { PAGE_BREAKPOINTS } from '@app/constants/commonConstants';
import styles from './ImageZoom.module.scss';

const ImageZoom = ({ children }) => {
  const [showZoom, setShowZoom] = useState(false);
  const resultRef = useRef();
  const lensRef = useRef();
  const ref = useRef(null);
  const containerRef = useRef(null);
  const isLG = useBreakpoint(PAGE_BREAKPOINTS.LG);

  // we probably need to calulate this original image / lens. Note the value may be differnt for x and y axis
  const zoomFactor = isLG ? 440 / 140 : 340 / 140;

  const closeZoom = useCallback(() => {
    setShowZoom(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLG]);
  const getCursorPos = useCallback(
    e => {
      let x = 0,
        y = 0;
      const a = ref.current.getBoundingClientRect();
      x = e.pageX - a.left;
      y = e.pageY - a.top;
      x = x - window.scrollX;
      y = y - window.scrollY;
      return { x, y };
    },
    [ref]
  );

  const moveZoom = useCallback(
    e => {
      setShowZoom(true);

      let pos, x, y;
      if (e.preventDefault) {
        e.preventDefault();
      }
      pos = getCursorPos(e);
      x = pos.x - lensRef.current.offsetWidth / 2;
      y = pos.y - lensRef.current.offsetHeight / 2;

      /* Prevent the lens from being positioned outside the image: */
      if (x > ref.current.width - lensRef.current.offsetWidth) {
        x = ref.current.width - lensRef.current.offsetWidth;
      }

      if (x < 0) {
        x = 0;
      }
      if (y > ref.current.height - lensRef.current.offsetHeight) {
        y = ref.current.height - lensRef.current.offsetHeight;
      }

      if (y < 0) {
        y = 0;
      }
      /* Set the position of the lens: */
      /*   Problem: img container is the position absolute but doesn't align with the img so the lens is not placed correctly. 
      Solution: we need to calculate the difference between the img and and the img container to place the lens correctly */
      const posContainer = containerRef.current.getBoundingClientRect();
      const posImage = ref.current.getBoundingClientRect();
      const xAdj = posImage.left - posContainer.left;
      const yAdj = posImage.top - posContainer.top;
      lensRef.current.style.left = x + xAdj + 'px';
      lensRef.current.style.top = y + yAdj + 'px';
      /* Display what the lens "sees": */
      const removePresetFromUrl = `${ref.current.src.split('?')[0]}${''}`;
      resultRef.current.style.backgroundImage =
        "url('" + removePresetFromUrl + "')";
      resultRef.current.style.backgroundSize =
        ref.current.width * zoomFactor +
        'px ' +
        ref.current.height * zoomFactor +
        'px';
      resultRef.current.style.backgroundPosition =
        '-' + x * zoomFactor + 'px -' + y * zoomFactor + 'px';
    },
    [getCursorPos, ref, zoomFactor]
  );

  const processTouch = useCallback(
    e => {
      e.preventDefault();
      if (e.changedTouches) {
        moveZoom(e.changedTouches[0]);
      }
    },
    [moveZoom]
  );

  //using a ref callback because the node isn't available initially
  const setRef = useCallback(
    node => {
      if (node) {
        node.addEventListener('mousemove', moveZoom);
        node.addEventListener('mouseleave', closeZoom);
        node.addEventListener('touchmove', processTouch);
        node.addEventListener('touchstart', processTouch);
        node.addEventListener('touchend', closeZoom);
        // this is to handle lazyload for an image
        const handleLoad = () => {
          resultRef.current.style.backgroundSize =
            node.width * zoomFactor + 'px ' + node.height * zoomFactor + 'px';
          resultRef.current.style.backgroundImage = "url('" + node.src + "')";
          ref.current.removeEventListener('load', handleLoad);
        };
        node.addEventListener('load', handleLoad);
        ref.current = node;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [closeZoom, moveZoom, zoomFactor, processTouch, isLG]
  );
  useEffect(() => {
    const lens = lensRef.current;
    lens.addEventListener('mouseleave', closeZoom);
    lens.addEventListener('mousemove', moveZoom);
    lens.addEventListener('touchmove', processTouch);
    lens.addEventListener('touchend', closeZoom);
    return () => {
      ref.current?.removeEventListener('mousemove', moveZoom);
      ref.current?.removeEventListener('touchmove', processTouch);
      ref.current?.removeEventListener('touchstart', processTouch);
      ref.current?.removeEventListener('mouseleave', closeZoom);
      ref.current?.removeEventListener('touchend', closeZoom);
      lens.removeEventListener('mouseleave', closeZoom);
      lens.removeEventListener('mousemove', moveZoom);
      lens.removeEventListener('touchmove', processTouch);
      lens.removeEventListener('touchend', closeZoom);
    };
  }, [closeZoom, moveZoom, processTouch, ref, zoomFactor]);

  return (
    <div
      ref={containerRef}
      className={cx('position-relative', styles['img-zoom-container'])}
    >
      <div
        ref={lensRef}
        className={cx(
          styles['lens'],
          'align-items-center justify-content-center position-absolute',
          { 'd-none': !showZoom, 'd-flex': showZoom }
        )}
      >
        <SearchIcon
          className="u-fill--MediumBlue"
          width={40}
          height={40}
          title=""
        />
      </div>
      {React.Children.map(children, child => {
        return React.cloneElement(child, { setRef });
      })}
      <div
        ref={resultRef}
        className={cx(styles['img-zoom-result'], 'position-absolute', {
          'd-none': !showZoom
        })}
      />
    </div>
  );
};

export default ImageZoom;

ImageZoom.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node
  ])
};
